import React from 'react';
import { useAccount, useAccountEffect, useConnections, useSwitchAccount } from 'wagmi';

// wagmi intentionally supports multiple simultaneous connections and `useAccount`
// surfaces whichever one is "current". RainbowKit's flag-based injected connectors
// (rabby / trust / binance …) fall back to `window.ethereum` when their own wallet
// isn't installed, and any wallet still authorized in its extension can reconnect, so
// wagmi briefly attaches extra connections to whatever wallet owns `window.ethereum`
// (e.g. OKX) and flips "current" to it — you connect TokenPocket but see OKX's address.
//
// We keep "current" pinned to the wallet the user actually picked. Crucially we do this
// with `switchAccount` (re-point current) rather than disconnecting the phantom: a
// disconnect makes the still-authorized wallet immediately reconnect, which produced an
// endless disconnect→reconnect loop that hung the UI. The leftover connections are
// harmless (current/display/signing all use the picked wallet) and are cleared by the
// disconnect-all in useWallet when the user disconnects.
export default function useEnforceSingleWallet() {
  const connections = useConnections();
  const { connector: currentConnector } = useAccount();
  const { switchAccount } = useSwitchAccount();

  // The connector the user explicitly connected. Phantom connections fired by the
  // reconnect probe do NOT trigger onConnect (account is already connected), so this ref
  // reliably holds the user's intended wallet.
  const keepUidRef = React.useRef<string | undefined>(undefined);

  useAccountEffect({
    onConnect({ connector, isReconnected }) {
      if (!isReconnected || keepUidRef.current === undefined) {
        keepUidRef.current = connector.uid;
      }
    },
    onDisconnect() {
      keepUidRef.current = undefined;
    },
  });

  React.useEffect(() => {
    const keepUid = keepUidRef.current;
    if (!keepUid || !currentConnector || currentConnector.uid === keepUid) {
      return;
    }
    const target = connections.find((connection) => connection.connector.uid === keepUid);
    if (target) {
      switchAccount({ connector: target.connector });
    }
  }, [ connections, currentConnector, switchAccount ]);
}
